import type { ReactNode } from 'react';

export type Size = '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

export type Shape = 'rounded' | 'pill' | 'square';

/** Status for async update operations (loading spinner, success checkmark, error warning). */
export type UpdateStatus = 'updating' | 'success' | 'error';

export type BaseProps = {
  size?: Size;
  bordered?: boolean;
  shape?: Shape;
  icon?: ReactNode;
  rIcon?: ReactNode;
  error?: string;
};

// ── Size utilities ──

export function sizeHeight(size: Size = 'md') {
  return {
    '3xs': 'h-4',
    '2xs': 'h-5',
    xs: 'h-6',
    sm: 'h-8',
    md: 'h-9',
    lg: 'h-10',
    xl: 'h-12',
    '2xl': 'h-16',
    '3xl': 'h-20',
    '4xl': 'h-24',
  }[size];
}

export function sizeWidth(size: Size = 'md') {
  return {
    '3xs': 'w-4',
    '2xs': 'w-5',
    xs: 'w-6',
    sm: 'w-8',
    md: 'w-9',
    lg: 'w-10',
    xl: 'w-12',
    '2xl': 'w-16',
    '3xl': 'w-20',
    '4xl': 'w-24',
  }[size];
}

export function sizeMinHeight(size: Size = 'md') {
  return {
    '3xs': 'min-h-4',
    '2xs': 'min-h-5',
    xs: 'min-h-6',
    sm: 'min-h-8',
    md: 'min-h-9',
    lg: 'min-h-10',
    xl: 'min-h-12',
    '2xl': 'min-h-16',
    '3xl': 'min-h-20',
    '4xl': 'min-h-24',
  }[size];
}

export function sizeFont(size: Size = 'md') {
  return {
    '3xs': 'text-[9px]',
    '2xs': 'text-[10px]',
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-base',
    '2xl': 'text-lg',
    '3xl': 'text-xl',
    '4xl': 'text-2xl',
  }[size];
}

export function sizeRounded(size: Size = 'md') {
  return {
    '3xs': 'rounded-sm',
    '2xs': 'rounded-md',
    xs: 'rounded-md',
    sm: 'rounded-lg',
    md: 'rounded-lg',
    lg: 'rounded-xl',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-2xl',
    '4xl': 'rounded-3xl',
  }[size];
}

export function shapeRounded(shape: Shape = 'rounded', size: Size = 'md') {
  if (shape === 'pill') return 'rounded-full';
  if (shape === 'square') return 'rounded-none';
  return sizeRounded(size);
}

export function sizePaddingX(size: Size = 'md') {
  return {
    '3xs': 'px-1',
    '2xs': 'px-1.5',
    xs: 'px-2',
    sm: 'px-2.5',
    md: 'px-3',
    lg: 'px-3.5',
    xl: 'px-4',
    '2xl': 'px-5',
    '3xl': 'px-6',
    '4xl': 'px-7',
  }[size];
}

export function sizePaddingL(size: Size = 'md') {
  return {
    '3xs': 'pl-1',
    '2xs': 'pl-1.5',
    xs: 'pl-2',
    sm: 'pl-2.5',
    md: 'pl-3',
    lg: 'pl-3.5',
    xl: 'pl-4',
    '2xl': 'pl-5',
    '3xl': 'pl-6',
    '4xl': 'pl-7',
  }[size];
}

export function sizePaddingR(size: Size = 'md') {
  return {
    '3xs': 'pr-1',
    '2xs': 'pr-1.5',
    xs: 'pr-2',
    sm: 'pr-2.5',
    md: 'pr-3',
    lg: 'pr-3.5',
    xl: 'pr-4',
    '2xl': 'pr-5',
    '3xl': 'pr-6',
    '4xl': 'pr-7',
  }[size];
}

export function sizeGap(size: Size = 'md') {
  return {
    '3xs': 'gap-0.5',
    '2xs': 'gap-0.5',
    xs: 'gap-1',
    sm: 'gap-1.5',
    md: 'gap-1.5',
    lg: 'gap-2',
    xl: 'gap-2',
    '2xl': 'gap-2.5',
    '3xl': 'gap-3',
    '4xl': 'gap-3.5',
  }[size];
}

// Inner gap = P/2, used between icon and text in form controls
export function sizeInnerGap(size: Size = 'md') {
  return {
    '3xs': 'gap-0.5',     // 4px / 2 = 2px
    '2xs': 'gap-0.5',     // 6px / 2 = 3px
    xs: 'gap-1',       // 8px / 2 = 4px
    sm: 'gap-[5px]',   // 10px / 2 = 5px
    md: 'gap-1.5',     // 12px / 2 = 6px
    lg: 'gap-[7px]',   // 14px / 2 = 7px
    xl: 'gap-2',       // 16px / 2 = 8px
    '2xl': 'gap-2.5',  // 20px / 2 = 10px
    '3xl': 'gap-3',    // 24px / 2 = 12px
    '4xl': 'gap-3.5',  // 28px / 2 = 14px
  }[size];
}

// Icon padding: calculated so icon-only button is a perfect square
// padding = (height - 2px_border - icon_size) / 2
export function sizeIconPaddingL(size: Size = 'md') {
  return {
    '3xs': 'pl-0.5',   // (16 - 2 - 10) / 2 = 2
    '2xs': 'pl-[3px]', // (20 - 2 - 12) / 2 = 3
    xs: 'pl-[5px]',   // (24 - 2 - 12) / 2 = 5
    sm: 'pl-[7px]',   // (32 - 2 - 16) / 2 = 7
    md: 'pl-[7px]',   // (36 - 2 - 20) / 2 = 7
    lg: 'pl-2',       // (40 - 2 - 22) / 2 = 8
    xl: 'pl-[11px]',  // (48 - 2 - 24) / 2 = 11
    '2xl': 'pl-[17px]', // (64 - 2 - 28) / 2 = 17
    '3xl': 'pl-[23px]', // (80 - 2 - 32) / 2 = 23
    '4xl': 'pl-[29px]', // (96 - 2 - 36) / 2 = 29
  }[size];
}

export function sizeIconPaddingR(size: Size = 'md') {
  return {
    '3xs': 'pr-0.5',   // (16 - 2 - 10) / 2 = 2
    '2xs': 'pr-[3px]', // (20 - 2 - 12) / 2 = 3
    xs: 'pr-[5px]',   // (24 - 2 - 12) / 2 = 5
    sm: 'pr-[7px]',   // (32 - 2 - 16) / 2 = 7
    md: 'pr-[7px]',   // (36 - 2 - 20) / 2 = 7
    lg: 'pr-2',       // (40 - 2 - 22) / 2 = 8
    xl: 'pr-[11px]',  // (48 - 2 - 24) / 2 = 11
    '2xl': 'pr-[17px]', // (64 - 2 - 28) / 2 = 17
    '3xl': 'pr-[23px]', // (80 - 2 - 32) / 2 = 23
    '4xl': 'pr-[29px]', // (96 - 2 - 36) / 2 = 29
  }[size];
}

export function sizeIconClass(size: Size = 'md') {
  return {
    '3xs': 'size-2.5',   // 10px (shrunk from 12px for 2px min padding)
    '2xs': 'size-3',     // 12px (shrunk from 14px for 3px padding)
    xs: 'size-3',     // 12px (shrunk from 16px for 5px padding)
    sm: 'size-4',     // 16px (shrunk from 20px for better proportion)
    md: 'size-5',     // 20px = text-sm line-height
    lg: 'size-[22px]', // 22px (shrunk from 24px for better proportion)
    xl: 'size-6',     // 24px = text-base line-height
    '2xl': 'size-7',  // 28px
    '3xl': 'size-8',  // 32px
    '4xl': 'size-9',  // 36px
  }[size];
}

// ── Misc utilities ──

/** Strip colons from React's useId() output (e.g. `:r1:` → `r1`) for use in CSS anchor names and HTML ids. */
export function cleanId(id: string) {
  return id.replace(/:/g, '');
}

// ── Group utilities ──

export function childRoundedClasses(isFirst: boolean, isLast: boolean, shape: Shape = 'rounded') {
  if (isFirst && isLast) return '';
  if (isFirst) return shape === 'pill' ? 'rounded-r-none! rounded-l-full!' : 'rounded-r-none!';
  if (isLast) return shape === 'pill' ? 'rounded-l-none! rounded-r-full!' : 'rounded-l-none!';
  return 'rounded-none!';
}
